import React, { useContext, useEffect, useState } from "react";
import { changeSettings, getEvents } from "../../api";
import { useLocation, useHistory } from "react-router-dom";
import DefaultThumbnail from "../../assets/images/DefaultThumbnail.png";
import "./style.scss";

// constants
import moment from "moment";
import { mapHighestRole } from "../../functions/styleAppliers";
import { Modal, Spinner } from "react-bootstrap";
import { Link } from "@mui/material";
import { Colors, EventConstants } from "../../constants";
import { UserContext } from "../../App";
import { toast } from "react-toastify";

const Events = (props) => {
	const { currentUserData, setCurrentUserData } = useContext<any>(UserContext);
	const [eventsChanged, setEventsChanged] = useState<boolean>(true);
	const [isQuerying, setIsQuerying] = useState<boolean>(false);
	const [events, setEvents] = useState<any>([]);
	const [notificationModalShow, setNotificationModalShow] = useState(false);
	const [notifiedGames, setNotifiedGames] = useState<string[]>(
		currentUserData?.settings?.notifications?.events || [],
	);
	const initialNotificationState =
		currentUserData?.settings?.notifications?.events || [];

	const useQuery = () => {
		return new URLSearchParams(useLocation().search);
	};
	const query: URLSearchParams = useQuery();
	const page: number = parseInt(query.get("page") || "1");

	useEffect(() => {
		document.title = "Contests & Events - Level Share Square";
	}, []);

	useEffect(() => {
		// stop if querying
		if (isQuerying) return setEventsChanged(false);

		if (eventsChanged) {
			setIsQuerying(true);
			// fetch
			getEvents(page)
				.then((res) => {
					// update documents
					setEvents(res?.data?.events);
				})
				.finally(() =>
					// prepare next cycle
					setIsQuerying(false),
				);
		}
	}, [eventsChanged, isQuerying, page]);

	const notificationDisplayModal = () => (
		<Modal
			show={notificationModalShow}
			onHide={() => setNotificationModalShow(false)}
			aria-labelledby="contained-modal-title-vcenter"
			size={notificationModalShow ? "xl" : "sm"}
			className="popup-modal"
			centered>
			<span className="modal-fill">
				{notificationModalShow ? (
					<>
						<Modal.Body className="modal-filter">
							<h2 className="center-textoutput">
								Select the games for which you want to receive
								event notifications for
							</h2>
							<hr />
							<MapNotifiableGames
								gameProperties={props.gameProperties}
								notifiedGames={notifiedGames}
								setNotifiedGames={setNotifiedGames}
							/>
						</Modal.Body>
						<Modal.Footer className="mx-auto modal-buttons modal-filter">
							<button
								className="btn btn-primary__danger"
								onClick={() => setNotificationModalShow(false)}>
								Close
							</button>
							<button
								className="btn btn-primary__special"
								onClick={handleEventNotificationUpdate}>
								Save
							</button>
						</Modal.Footer>
					</>
				) : (
					<h3 style={{ textAlign: "center", padding: "20px" }}>
						Closing...
					</h3>
				)}
			</span>
		</Modal>
	);

	const handleEventNotificationUpdate = () => {
		// make sure changes were made
		if (
			initialNotificationState.every((game) =>
				notifiedGames.includes(game),
			) &&
			initialNotificationState.length === notifiedGames.length
		)
			return toast.error("No changes were made!");

		// make api call
		changeSettings({
			value: notifiedGames,
			type: "events",
			family: "notifications",
		}).then((res) => {
			// update user
			setCurrentUserData((prev) => ({
				...prev,
				settings: res?.data?.settings,
			}));
			setNotificationModalShow(false);
		});
	};

	// no events found
	if (!events?.length)
		return (
			<div className="container mt-4">
				<div className="jumbotron d-flex justify-content-center">
					<h1>Contests & Events</h1>
				</div>
				<br />
				{!eventsChanged && !isQuerying ? (
					<div className="card card-body center-textoutput">
						<h2>No events found.</h2>
					</div>
				) : (
					<div className="d-flex justify-content-center">
						<Spinner animation="grow" variant="primary" />
					</div>
				)}
			</div>
		);

	// render events page
	return (
		<div className="container mt-4">
			<div
				className="jumbotron d-flex justify-content-center"
				style={{ flexDirection: "column" }}>
				<div className="d-flex justify-content-center">
					<h1>Contests & Events</h1>
				</div>
				{currentUserData?._id && (
					<>
						<hr />
						<div className="d-flex justify-content-center">
							<button
								className="btn btn-primary"
								onClick={() => setNotificationModalShow(true)}>
								Event notifications...
							</button>
						</div>
					</>
				)}
			</div>
			<br />
			<div className="events-grid">
				{events?.map((event) => (
					<React.Fragment key={event?._id}>
						<MapEvents event={event} />
					</React.Fragment>
				))}
			</div>
			{notificationDisplayModal()}
		</div>
	);
};

const MapEvents = (props) => {
	const history = useHistory();
	const event = props?.event;

	const active: boolean = event?.active;
	const submissions: number =
		event?.submissionType === "poll"
			? event?.pollVotes || 0
			: event?.submissionCount || 0;
	const startDate: Date = new Date(event?.startDate);
	const endDate: Date = new Date(event?.endDate);
	const appendHours: string = active ? " - h:mm A" : "";
	const primaryHost: any = event?.hosts?.[0];
	const authorRoles: string[] = primaryHost?.mainRoles || [];
	const roleColor: string = mapHighestRole(authorRoles, "color");
	const thumbnail: string = event?.thumbnail || DefaultThumbnail;
	const type: string = event?.type || "Contest";
	const hrefValue: string = `/events/${event?._id}`;
	const oneHourAfterStart: Date = new Date(startDate.getTime() + 3600 * 1000);
	const afterStartDate: boolean = oneHourAfterStart <= new Date();

	// status
	const eventStatusId = active
		? 1
		: !afterStartDate
			? 0
			: event?.hasResults
				? 3
				: 2;
	const eventStatuses = ["Unstarted", "Active", "Results pending", "Ended"];
	const eventStatus = eventStatuses[eventStatusId] || "Unknown";

	const sType = EventConstants.SubmTypes;
	const typeMap = {
		[sType.LVL]: "Level contest",
		[sType.MINILVL]: "Mini contest",
		[sType.TEXT]: "Writing event",
		[sType.IMAGE]: "Art contest",
		[sType.POLL]: "Polling event",
	};

	return (
		<div
			className="card event-card"
			style={{ overflow: "hidden", border: "unset" }}>
			<Link
				onClick={(e) => {
					// let it open in new tab
					if (e.ctrlKey) return;
					e.preventDefault();
					history.push(hrefValue);
					window.scrollTo(0, 0);
				}}
				href={hrefValue}>
				<button
					className={`btn btn-primary${
						[0, 1].includes(eventStatusId)
							? "__special" // green if active
							: eventStatusId === 2
								? "__danger" // red when results pending
								: "" // otherwise blue
					}`}
					style={{
						borderRadius: "0px",
						fontStyle: "bold",
						width: "100%",
					}}>
					{event?.name}
				</button>
				<div
					className="card-body event-body"
					style={{
						backgroundImage: `url("${thumbnail}")`,
					}}>
					<div className="event-text unselectable">
						<b
							className={`event-text__item ${Colors.Array.R_O_G[eventStatusId - 1]}`}>
							{eventStatus} - {typeMap[event?.submissionType]}
						</b>
						<span className="event-text__item">
							Start:{" "}
							<span className="blue">
								{moment(startDate).format(
									`MMM D, YYYY${appendHours}`,
								)}
							</span>
						</span>
						<span className="event-text__item">
							End:{" "}
							<span className="blue">
								{moment(endDate).format(
									`MMM D, YYYY${appendHours}`,
								)}
							</span>
						</span>
						<span className="event-text__item">
							{submissions}{" "}
							{event?.submissionType === "poll"
								? "vote"
								: "submission"}
							{submissions === 1 ? "" : "s"}
						</span>
						<span
							className="event-text__item yellow"
							style={{ marginTop: "10px" }}>
							{type} hosted by
						</span>
						<span className="event-text__item">
							<span
								className="main-roles"
								style={{
									color: roleColor,
									borderColor: roleColor,
									boxShadow: `0 0 1px 1px ${roleColor}`,
									position: "relative",
									padding: "0 4px",
								}}>
								{mapHighestRole(authorRoles, "role")}
							</span>{" "}
							{primaryHost?.username}
						</span>
					</div>
				</div>
			</Link>
		</div>
	);
};

const MapNotifiableGames = ({
	gameProperties,
	notifiedGames,
	setNotifiedGames,
}) => (
	<div
		className="unselectable"
		style={{
			display: "flex",
			flexWrap: "wrap",
			gap: "8px",
			justifyContent: "center",
		}}>
		<div
			className={`game-notification-logo ${notifiedGames.length === gameProperties.length - 1 ? "active" : ""}`}
			style={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
			}}
			onClick={() => {
				if (notifiedGames.length === gameProperties.length - 1) {
					setNotifiedGames([]);
				} else {
					setNotifiedGames(
						gameProperties
							.map((game) => game?.acronym)
							.filter((acronym) => acronym !== "SMF"),
					);
				}
			}}>
			<h1>ALL</h1>
		</div>
		{gameProperties.map((game, index) =>
			game?.internalID === 3 ? null : (
				<React.Fragment key={index}>
					<img
						className={`game-notification-logo ${notifiedGames.includes(game?.acronym) ? "active" : ""}`}
						src={game?.cleanLogo}
						onClick={() => {
							setNotifiedGames((prev) =>
								notifiedGames.includes(game?.acronym)
									? prev.filter((g) => g !== game?.acronym)
									: [...prev, game?.acronym],
							);
						}}
						alt={game?.name}
					/>
				</React.Fragment>
			),
		)}
	</div>
);

export default Events;
